import { datadogRum } from '@datadog/browser-rum';
import useToast from '@hooks/ui/useToast';
import { useFlow } from '@router/stackflow';
import { RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.scss';

const REFLOW_DURATION_MS = 50;
const VISIBLE_ANIMATION_DURATION_MS = 350;
const CARD_ANIMATION_DURATION_MS = 1700;

interface Props {
    ref: RefObject<HTMLDivElement|null>;
    cardRef: RefObject<HTMLImageElement|null>;
    imageUrl?: string;
  userPrepaidCardId: string;
}

function useCompleteAnimation({
    ref, cardRef, imageUrl, userPrepaidCardId,
}:Props) {
    const [isVisible, setIsVisible] = useState(false);
    const { replace, push } = useFlow();
    const toast = useToast();
    const { t } = useTranslation('prepaidCard');
    useEffect(() => {
        if (imageUrl) {
            const img = document.createElement('img');
            img.onload = () => {
                setIsVisible(true);
            };
            img.onerror = () => {
                toast.error(t('orderComplete.error.imageLoadFailed'));
                setIsVisible(true);
            };
            img.src = imageUrl;
        }
    }, [imageUrl, t, toast]);
    useEffect(() => {
        if (isVisible) {
            if (import.meta.env.MODE !== 'development') {
                datadogRum.addAction('prepaidCardOrderComplete', {
                    userPrepaidCardId,
                });
            }
            ref.current?.classList.remove(styles.none);
            const visibleTimeout = setTimeout(() => {
                ref.current?.classList.remove(styles.hide);
            }, REFLOW_DURATION_MS);
            const cardTimeout = setTimeout(() => {
                cardRef.current?.classList.add(styles.animate);
            }, VISIBLE_ANIMATION_DURATION_MS);
            const moveTimeout = setTimeout(() => {
                replace('MainActivity', {}, { animate: false });
                push('MyPrepaidCardActivity', {});
            }, VISIBLE_ANIMATION_DURATION_MS + CARD_ANIMATION_DURATION_MS);
            return () => {
                clearTimeout(visibleTimeout);
                clearTimeout(cardTimeout);
                clearTimeout(moveTimeout);
            };
        }
        return () => {};
    }, [cardRef, isVisible, ref, replace, push]);
}

export default useCompleteAnimation;
